// Consistent layout wrapper
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  UsersIcon,
  UserGroupIcon,
  ChartBarIcon,
  Bars3Icon,
  XMarkIcon,
  CreditCardIcon,
  ScaleIcon,
  ClipboardDocumentListIcon,
  ShareIcon,
  SpeakerWaveIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { usePendingAdminUsers } from '@/hooks/useAdminUsers';
import DevIndicator from './DevIndicator';
import ProfileDropdown from './ProfileDropdown';

interface AdminLayoutProps {
  children: React.ReactNode;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: typeof HomeIcon;
  current?: boolean;
  requiresSuperAdmin?: boolean;
  external?: boolean;
}

const baseNavigation: NavigationItem[] = [
  { name: 'Dashboard', href: '/', icon: HomeIcon },
  { name: 'Users', href: '/users', icon: UsersIcon },
  { name: 'Teams', href: '/teams', icon: BuildingOfficeIcon },
  { name: 'Analytics', href: '/analytics', icon: ChartBarIcon },
  { name: 'Payments', href: '/payments', icon: CreditCardIcon },
  { name: 'Quotas & Tiers', href: '/quotas', icon: ScaleIcon },
  { name: 'Referrals', href: '/referrals', icon: ShareIcon },
  { name: 'Announcements', href: '/announcements', icon: SpeakerWaveIcon },
  { name: 'Forum', href: '/forum', icon: ChatBubbleLeftRightIcon },
  { name: 'Emails', href: '/emails', icon: EnvelopeIcon },
  { name: 'Admin Users', href: '/admin-users', icon: UserGroupIcon },
  { name: 'Audit Logs', href: '/audit-logs', icon: ClipboardDocumentListIcon },
];

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const savedWidth = localStorage.getItem('admin-sidebar-width');
    if (savedWidth) {
      const width = parseInt(savedWidth, 10);
      if (width >= 180 && width <= 350) {
        return width;
      }
    }
    return 220;
  });
  const [isResizing, setIsResizing] = useState(false);
  const [isDesktop, setIsDesktop] = useState(true);
  const { user, signOut } = useAdminAuth();
  const location = useLocation();

  // Save sidebar width to localStorage
  useEffect(() => {
    localStorage.setItem('admin-sidebar-width', sidebarWidth.toString());
  }, [sidebarWidth]);

  // Handle resize start
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  // Handle resize move
  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;

    const newWidth = e.clientX;
    if (newWidth >= 180 && newWidth <= 350) {
      setSidebarWidth(newWidth);
    }
  }, [isResizing]);

  // Handle resize end
  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  // Add mouse event listeners for resizing
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);

      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // Handle responsive breakpoint
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };

    handleResize(); // Set initial value
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check if current user is super admin
  const isSuperAdmin = user?.email === 'young@nurie.ai' || user?.roles?.includes('super_admin');

  // Get pending admin users count for notification badge
  const { data: pendingUsers } = usePendingAdminUsers();
  const pendingCount = pendingUsers?.data?.length || 0;

  // Filter navigation based on user permissions
  const navigation = baseNavigation.filter(item => {
    if (item.requiresSuperAdmin && !isSuperAdmin) {
      return false;
    }
    return true;
  });

  // Update current navigation item based on current path
  // Support nested routes (e.g., /emails/send-now should highlight "Emails")
  const navigationWithCurrent = navigation.map(item => ({
    ...item,
    current: location.pathname === item.href || location.pathname.startsWith(item.href + '/'),
  }));

  return (
    <div className="h-full">
      {/* Development Environment Indicator */}
      <DevIndicator />

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 flex z-40 md:hidden" role="dialog" aria-modal="true">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" aria-hidden="true"></div>

          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                type="button"
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setSidebarOpen(false)}
              >
                <span className="sr-only">Close sidebar</span>
                <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
              </button>
            </div>

            <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
              <div className="flex-shrink-0 flex items-center px-4">
                <h1 className="text-2xl font-bold text-gray-900">MediaSage Ops</h1>
              </div>
              <nav className="mt-5 px-2 space-y-1">
                {navigationWithCurrent.map((item) => {
                  const className = `${item.current
                      ? 'bg-gray-100 text-gray-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    } group flex items-center px-2 py-2 text-base font-medium rounded-md`;

                  const content = (
                    <>
                      <item.icon
                        className={`${item.current ? 'text-gray-500' : 'text-gray-400 group-hover:text-gray-500'
                          } mr-4 h-6 w-6`}
                        aria-hidden="true"
                      />
                      <span className="flex-1">{item.name}</span>
                      {item.name === 'Admin Users' && pendingCount > 0 && (
                        <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          {pendingCount}
                        </span>
                      )}
                    </>
                  );

                  return item.external ? (
                    <a
                      key={item.name}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={className}
                      onClick={() => setSidebarOpen(false)}
                    >
                      {content}
                    </a>
                  ) : (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={className}
                      onClick={() => setSidebarOpen(false)}
                    >
                      {content}
                    </Link>
                  );
                })}
              </nav>
            </div>

            <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                    <span className="text-sm font-medium text-gray-700">
                      {user?.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-700">{user?.name}</p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Static sidebar for desktop */}
      <div
        className="hidden md:flex md:flex-col md:fixed md:inset-y-0"
        style={{ width: `${sidebarWidth}px` }}
      >
        <div className="flex-1 flex flex-col min-h-0 border-r border-gray-200 bg-white relative">
          <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
            <div className="flex items-center flex-shrink-0 px-4">
              <h1 className="text-2xl font-bold text-gray-900">MediaSage Ops</h1>
            </div>
            <nav className="mt-5 flex-1 px-2 bg-white space-y-1">
              {navigationWithCurrent.map((item) => {
                const className = `${item.current
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } group flex items-center px-2 py-2 text-sm font-medium rounded-md`;

                const content = (
                  <>
                    <item.icon
                      className={`${item.current ? 'text-gray-500' : 'text-gray-400 group-hover:text-gray-500'
                        } mr-3 h-6 w-6`}
                      aria-hidden="true"
                    />
                    <span className="flex-1">{item.name}</span>
                    {item.name === 'Admin Users' && pendingCount > 0 && (
                      <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        {pendingCount}
                      </span>
                    )}
                  </>
                );

                return item.external ? (
                  <a
                    key={item.name}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={className}
                  >
                    {content}
                  </a>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={className}
                  >
                    {content}
                  </Link>
                );
              })}
            </nav>
          </div>

          <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
            <div className="flex items-center w-full">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                  <span className="text-sm font-medium text-gray-700">
                    {user?.name.charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-700 truncate">{user?.name}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
              <button
                onClick={signOut}
                className="ml-2 p-1 text-gray-400 hover:text-gray-600"
                title="Sign out"
              >
                <span className="sr-only">Sign out</span>
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          </div>

          {/* Resize handle */}
          <div
            className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500 hover:w-1.5 transition-all duration-150 group"
            onMouseDown={handleResizeStart}
            title="Drag to resize sidebar"
          >
            <div className="w-full h-full bg-transparent group-hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-150"></div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div
        className="flex flex-col flex-1"
        style={{ paddingLeft: isDesktop ? `${sidebarWidth}px` : '0' }}
      >
        <div className="sticky top-0 z-10 md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3 bg-gray-100">
          <button
            type="button"
            className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        {/* Top navigation bar */}
        <div className="bg-white shadow">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              <div className="flex items-center">
                <div className="flex-shrink-0 md:hidden">
                  <h1 className="text-xl font-bold text-gray-900">MediaSage Ops</h1>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                {/* User profile dropdown - desktop only */}
                <div className="hidden md:block">
                  <ProfileDropdown />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;



